import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePlatformSettings } from '../../context/PlatformSettingsContext';
import { fetchSaasPlans, resendAdminConfirmationEmail } from '../../services/auth/authApi';
import { getPriceForPlan } from '../../services/owner/subscriptionsApi';
import AuthHexBackground from './AuthHexBackground';
import PasswordField from './PasswordField';
import './Auth.css';

const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Yearly', 'Lifetime'];
const DEFAULT_TRIAL_DAYS = 7;

function planHasBillingCycle(plan, billingCycle) {
  return getPriceForPlan(plan.id, billingCycle, [plan]) > 0;
}

function cyclePriceLabel(plan, billingCycle, formatPrice) {
  const price = getPriceForPlan(plan.id, billingCycle, [plan]);
  if (!price) return plan.name;
  if (billingCycle === 'Lifetime') return `${plan.name} — ${formatPrice(price)} once`;
  const suffix = billingCycle === 'Monthly' ? '/mo' : billingCycle === 'Quarterly' ? '/qtr' : '/yr';
  return `${plan.name} — ${formatPrice(price)}${suffix}`;
}

function Register() {
  const { register, isAuthenticated, dashboardPath, loading } = useAuth();
  const { formatPrice } = usePlatformSettings();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [form, setForm] = useState({
    gymName: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    planId: '',
    billingCycle: 'Monthly',
    useFreeTrial: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPlansLoading(true);
        const list = await fetchSaasPlans();
        if (cancelled) return;
        setPlans(list);
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const availableCycles = useMemo(() => {
    const cycles = BILLING_CYCLES.filter((cycle) =>
      plans.some((plan) => planHasBillingCycle(plan, cycle)),
    );
    return cycles.length ? cycles : ['Monthly'];
  }, [plans]);

  const filteredPlans = useMemo(
    () => plans.filter((plan) => planHasBillingCycle(plan, form.billingCycle)),
    [plans, form.billingCycle],
  );

  // Keep billing cycle + plan selection valid as packages load
  useEffect(() => {
    if (!plans.length) return;

    setForm((prev) => {
      const cycle = availableCycles.includes(prev.billingCycle)
        ? prev.billingCycle
        : availableCycles[0];
      const plansForCycle = plans.filter((plan) => planHasBillingCycle(plan, cycle));
      const planStillValid = plansForCycle.some((plan) => plan.id === prev.planId);
      const nextPlanId = planStillValid ? prev.planId : (plansForCycle[0]?.id || '');

      if (cycle === prev.billingCycle && nextPlanId === prev.planId) return prev;
      return { ...prev, billingCycle: cycle, planId: nextPlanId };
    });
  }, [plans, availableCycles]);

  if (!loading && isAuthenticated) {
    return <Navigate to={dashboardPath} replace />;
  }

  const selectedPlan = filteredPlans.find((p) => p.id === form.planId)
    || plans.find((p) => p.id === form.planId);
  const trialDays = selectedPlan?.trialDays || DEFAULT_TRIAL_DAYS;
  const displayPrice = form.useFreeTrial
    ? 0
    : getPriceForPlan(form.planId, form.billingCycle, plans);

  const planFeatures = String(selectedPlan?.features || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'billingCycle') {
        const plansForCycle = plans.filter((plan) => planHasBillingCycle(plan, value));
        const stillValid = plansForCycle.some((plan) => plan.id === prev.planId);
        next.planId = stillValid ? prev.planId : (plansForCycle[0]?.id || '');
      }

      return next;
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.planId) {
      setError('Please select a subscription plan.');
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    setResendNote('');

    try {
      const result = await register({
        gymName: form.gymName,
        fullName: form.fullName,
        email: form.email,
        password: form.password,
        planId: form.planId,
        billingCycle: form.billingCycle,
        useFreeTrial: form.useFreeTrial,
      });

      if (result.needsEmailConfirmation) {
        const email = form.email.trim().toLowerCase();
        setPendingConfirmEmail(email);
        setSuccess(
          `Account created! A confirmation link was sent to ${email}. Open it, then sign in. Check Spam / Promotions too (Gmail often moves auth mail there).`,
        );
        return;
      }

      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!pendingConfirmEmail) return;
    try {
      setResendBusy(true);
      setResendNote('');
      await resendAdminConfirmationEmail(pendingConfirmEmail);
      setResendNote(`Confirmation email sent again to ${pendingConfirmEmail}. Check inbox and Spam.`);
    } catch (err) {
      setResendNote(err.message || 'Could not resend email.');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <AuthHexBackground />
      <div className="auth-card auth-card-wide auth-card-neon">
        <div className="auth-brand">
          <h1>Register Your Gym</h1>
          <p>Create a gym admin account and choose a subscription plan</p>
        </div>

        {error && <p className="auth-error" role="alert">{error}</p>}
        {success && <p className="auth-success" role="status">{success}</p>}
        {success && pendingConfirmEmail && (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            <button
              type="button"
              className="owner-btn owner-btn-secondary"
              onClick={handleResendConfirmation}
              disabled={resendBusy}
            >
              {resendBusy ? 'Sending…' : 'Resend confirmation email'}
            </button>
            {resendNote && (
              <p className="auth-success" role="status" style={{ margin: 0 }}>{resendNote}</p>
            )}
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted, #64748b)' }}>
              Wait 1–2 minutes. If the email is in Spam, switch Supabase Custom SMTP from Gmail to Resend
              (smtp.resend.com) — see supabase/email-templates/SMTP_KEEP_CONFIRM_NO_RATE_LIMIT.txt.
              Then paste the updated confirmation.html. Keep Confirm email ON. App code cannot force Primary inbox.
            </p>
            <Link to="/" className="auth-link">Back to sign in</Link>
          </div>
        )}

        {!success && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="reg-gym">Gym Name</label>
              <input
                id="reg-gym"
                name="gymName"
                className="owner-form-input"
                value={form.gymName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="reg-name">Your Name</label>
              <input
                id="reg-name"
                name="fullName"
                className="owner-form-input"
                value={form.fullName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="reg-email">Admin Email</label>
              <input
                id="reg-email"
                name="email"
                type="email"
                className="owner-form-input"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>
            <div className="owner-grid-2">
              <PasswordField
                id="reg-password"
                name="password"
                label="Password"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
                minLength={6}
                required
              />
              <PasswordField
                id="reg-confirm"
                name="confirmPassword"
                label="Confirm Password"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />
            </div>

            {plansLoading ? (
              <p className="auth-trial-hint">Loading subscription packages...</p>
            ) : plans.length === 0 ? (
              <p className="auth-error" role="alert">
                No subscription packages are available yet. Contact the platform owner.
              </p>
            ) : (
              <>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="reg-billing">Billing Cycle</label>
                  <select
                    id="reg-billing"
                    name="billingCycle"
                    className="owner-form-select"
                    value={form.billingCycle}
                    onChange={handleChange}
                    required
                  >
                    {availableCycles.map((cycle) => (
                      <option key={cycle} value={cycle}>{cycle}</option>
                    ))}
                  </select>
                </div>

                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="reg-plan">Subscription Plan</label>
                  {filteredPlans.length === 0 ? (
                    <p className="auth-trial-hint">
                      No plans available for {form.billingCycle} billing. Choose another cycle.
                    </p>
                  ) : (
                    <select
                      id="reg-plan"
                      name="planId"
                      className="owner-form-select"
                      value={form.planId}
                      onChange={handleChange}
                      required
                    >
                      {filteredPlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {cyclePriceLabel(plan, form.billingCycle, formatPrice)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {selectedPlan && planFeatures.length > 0 && (
                  <div className="auth-plan-features">
                    <div className="auth-plan-features-title">Included in {selectedPlan.name}</div>
                    <ul>
                      {planFeatures.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="auth-trial-box">
                  <label className="auth-trial-label" htmlFor="reg-trial">
                    <input
                      id="reg-trial"
                      name="useFreeTrial"
                      type="checkbox"
                      checked={form.useFreeTrial}
                      onChange={handleChange}
                    />
                    <span>
                      Start with a <strong>{trialDays}-day free trial</strong>
                      {selectedPlan ? ` on ${selectedPlan.name}` : ''} (optional)
                    </span>
                  </label>
                </div>

                <p className="auth-price">
                  {form.useFreeTrial ? (
                    <>
                      Today: <strong>Free</strong> ({trialDays}-day trial)
                    </>
                  ) : (
                    <>
                      Price: <strong>{formatPrice(displayPrice)}</strong>
                      {form.billingCycle === 'Lifetime'
                        ? ' once'
                        : ` / ${form.billingCycle.toLowerCase()}`}
                    </>
                  )}
                </p>
              </>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={submitting || plansLoading || plans.length === 0 || !form.planId}
            >
              {submitting ? 'Creating account… (may retry if email is busy)' : form.useFreeTrial ? 'Start Free Trial' : 'Register & Subscribe'}
            </button>
          </form>
        )}

        {success && (
          <Link to="/" className="auth-submit" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Go to Sign In
          </Link>
        )}

        <p className="auth-footer">
          Already have an account? <Link to="/">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
